import React, { useState } from "react";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { FREDOKA, NO, Mascot, StepHeading, ContinueSlot, ContinueButton } from "../NOKit";

// iOS NOFirstAdventureStep + NOStoryGenerationStep port. A lesson (free text
// or one-tap chip) OR a ready-made adventure — mutually exclusive. The CTA
// runs completeOnboarding() behind a cosmetic "story being made" loader; the
// choice is stashed as a CreateStory draft so their first visit to /create is
// prefilled.
const IDEA_CHIPS = [
  "Sharing with friends",
  "Being brave",
  "Telling the truth",
  "Trying new foods",
  "Bedtime is fun",
  "Kindness wins",
];

// Generated theme art (gpt-image, Pixley story-cover style), bundled as
// local assets; emoji tile is the fallback if an image fails to load.
export const ADVENTURES = [
  { id: "pirate_treasure", emoji: "🏴‍☠️", title: "Pirate Treasure Hunt", thumb: "/assets/adventure-pirate.jpg" },
  { id: "space_mission", emoji: "🚀", title: "Space Mission", thumb: "/assets/adventure-space.jpg" },
  { id: "dino_discovery", emoji: "🦕", title: "Dinosaur Discovery", thumb: "/assets/adventure-dino.jpg" },
  { id: "ocean_quest", emoji: "🌊", title: "Ocean Quest", thumb: "/assets/adventure-ocean.jpg" },
];

const AdventureStep = () => {
  const { username, setAdventure, completeOnboarding, isLoading } = useOnboarding();
  const [lesson, setLesson] = useState("");
  const [theme, setTheme] = useState(null);
  const [making, setMaking] = useState(false);

  const canContinue = (lesson.trim().length >= 3 || !!theme) && !making && !isLoading;

  const handleMake = async () => {
    if (!canContinue) return;
    const adventure = theme
      ? { theme: theme.id, theme_title: theme.title }
      : { lesson: lesson.trim() };
    setAdventure(adventure);
    setMaking(true);
    try {
      await completeOnboarding(adventure);
    } finally {
      setMaking(false);
    }
  };

  if (making || isLoading) {
    return (
      <div className="w-full max-w-xl mx-auto flex flex-col items-center px-4 animate-fadeIn text-center">
        <Mascot size={200} clip="panda_paint_2" />
        <h1
          style={{
            ...FREDOKA,
            color: NO.ink,
            fontWeight: 700,
            fontSize: "clamp(28px, 5vw, 34px)",
            marginTop: 20,
          }}
        >
          {username ? `${username}'s` : "Your"} first story is{" "}
          <span style={{ color: NO.accent }}>being made</span>
        </h1>
        <p style={{ ...FREDOKA, color: NO.subtitle, fontSize: 17, marginTop: 10 }}>
          Setting up your Pixley world…
        </p>
        <div
          style={{
            marginTop: 26,
            width: 220,
            height: 10,
            borderRadius: 999,
            background: NO.track,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "40%",
              borderRadius: 999,
              background: `linear-gradient(90deg, ${NO.accent}, #00CAFF)`,
              animation: "ob-indeterminate 1.4s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      <Mascot size={100} clip="panda_wave_heart" />
      <div className="w-full" style={{ marginTop: 14 }}>
        <StepHeading
          eyebrow="First Story"
          title={`What should ${username || "your child"}`}
          accent="learn?"
        />
      </div>

      <div className="w-full" style={{ marginTop: 20 }}>
        <textarea
          value={lesson}
          maxLength={140}
          rows={2}
          placeholder="e.g. why brushing teeth is important…"
          onChange={(e) => {
            setLesson(e.target.value);
            if (e.target.value.trim()) setTheme(null);
          }}
          className="w-full resize-none"
          style={{
            ...FREDOKA,
            fontSize: 17,
            color: NO.ink,
            background: "#FFFFFF",
            padding: "14px 16px",
            borderRadius: 16,
            border: `2px solid ${theme ? NO.cardStroke : NO.track}`,
            outline: "none",
            boxShadow: "0 4px 8px rgba(0,0,0,0.04)",
            opacity: theme ? 0.6 : 1,
          }}
          onFocus={(e) => (e.target.style.border = `2px solid ${NO.accent}`)}
          onBlur={(e) => (e.target.style.border = `2px solid ${NO.track}`)}
        />
        <div className="flex flex-wrap gap-2" style={{ marginTop: 10 }}>
          {IDEA_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => {
                setLesson(chip);
                setTheme(null);
              }}
              className="transition active:scale-[0.95]"
              style={{
                ...FREDOKA,
                fontSize: 13,
                fontWeight: 600,
                color: lesson === chip ? "#FFFFFF" : NO.accent,
                background: lesson === chip ? NO.accent : "rgba(0,101,248,0.08)",
                border: "none",
                borderRadius: 999,
                padding: "8px 14px",
                cursor: "pointer",
              }}
            >
              {chip}
            </button>
          ))}
        </div>
      </div>

      <div
        className="w-full flex items-center gap-3"
        style={{ ...FREDOKA, color: NO.subtitle, fontSize: 13, fontWeight: 600, marginTop: 20 }}
      >
        <span className="flex-1" style={{ height: 1, background: NO.track }} />
        or pick a ready-made adventure
        <span className="flex-1" style={{ height: 1, background: NO.track }} />
      </div>

      <div className="w-full grid grid-cols-2 gap-3" style={{ marginTop: 14 }}>
        {ADVENTURES.map((a) => {
          const selected = theme?.id === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                setTheme(selected ? null : a);
                if (!selected) setLesson("");
              }}
              className="group relative transition active:scale-[0.97] text-left overflow-hidden"
              style={{
                borderRadius: 18,
                border: selected ? `3px solid ${NO.accent}` : "3px solid transparent",
                boxShadow: selected
                  ? "0 10px 22px rgba(0,101,248,0.30)"
                  : "0 4px 10px rgba(11,26,51,0.10)",
                padding: 0,
                cursor: "pointer",
                background: "#E3F0FF",
              }}
            >
              <div className="w-full overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
                <img
                  src={a.thumb}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.06]"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.style.background = "#E3F0FF";
                    e.currentTarget.parentElement.parentElement.querySelector(".adventure-fallback").style.display = "flex";
                  }}
                />
                <div
                  className="adventure-fallback absolute inset-0 items-center justify-center"
                  style={{ display: "none", fontSize: 56 }}
                >
                  {a.emoji}
                </div>
              </div>
              {/* Bottom scrim with title, like a real story card */}
              <div
                className="absolute bottom-0 left-0 right-0 flex items-end"
                style={{
                  padding: "26px 12px 10px",
                  background: "linear-gradient(180deg, rgba(11,26,51,0) 0%, rgba(11,26,51,0.72) 78%)",
                }}
              >
                <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 16, textShadow: "0 1px 4px rgba(0,0,0,0.35)" }}>
                  {a.emoji} {a.title}
                </span>
              </div>
              {selected && (
                <span
                  className="absolute flex items-center justify-center"
                  style={{
                    top: 8,
                    right: 8,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: NO.accent,
                    color: "#FFFFFF",
                    fontSize: 14,
                    fontWeight: 800,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  }}
                >
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>

      <ContinueSlot>
        <ContinueButton
          onClick={handleMake}
          disabled={!canContinue}
          label={username ? `Make ${username}'s First Story` : "Make the First Story"}
        />
      </ContinueSlot>
    </div>
  );
};

export default AdventureStep;
